/* eslint-disable no-unused-vars */
// @flow
import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import styles from './styles.module.scss';
import { H4, H6, Switch } from '../../components';
import Toggler from '../../components/Toggler';
import Select from '../../components/Select';
import Card from '../../components/Card';
import ResultStatistics from './ResultStatistics';
import Multicheckbox from '../../components/Multicheckbox';
import Check from 'components/Multicheckbox/Check';
import PressButton from 'components/PressButton/PressButton';
import FreeModal from 'components/FreeModal/FreeModal';
import { useTranslation } from 'react-i18next';

type Props = {
  limit: string,
  disable: boolean,
  setLimit: () => void,
  firstTogglerOptions: Array<any>,
  thirdTogglerOptions: Array<any>,
  stackSize: any,
  setStackSize: () => void,
  secondTogglerOptions: Array<any>,
  category: string,
  setCategory: () => void,
  positionsGroupedOptions: any,
  selectedPositions: any,
  setSelectedPositions: () => void,
  selectedHandTypes: any,
  setSelectedHandTypes: () => void,
  disabledPositions: any,
  limitIsHU: boolean,
  avoidDelimiters: boolean,
  handType: any,
  setHandType: () => void,
  handTypeSelector: any,
  getResultData: (any) => any,
  setGameResults: (any) => any,
  gameResults: any,
  isSmartMode: boolean,
  setSmartMode: any,
  mttIsDisabled: boolean,
  stack20isDisabled: boolean,
  huIsDisabled: boolean,
  isAdaptive?: true,
  gameOption: any,
  setGameOption: () => void,
  gameOptions: any,
  isPlo5: boolean,
  fastHands: boolean,
  setFastHands: () => void,
  timerIsActive: boolean,
  setTimerIsActive: () => void,
  ggps: string,
  setGgps: () => void,
  isFreeUser: boolean,
  difficulty: any,
  setDifficulty: () => void,
  difficultyOptions: any,
  isMTTAntePotSQZ?: boolean,
  isMTTAntePot10?: boolean,
  difficultyIsDisabled?: boolean,
  sessionActive?: boolean,
}

// eslint-disable-next-line react/prop-types
const TitleText = ({ isFreeUser }) => {
  const { t } = useTranslation();
  return isFreeUser ? 'FREE TRAINING' : t('challengeSaveHint');
};

const ModalText = () => {
  const [language, setLanguage] = useState<string>('');
  const getLanguage = () => {
    const lang = localStorage.getItem('i18nextLng');
    if (lang?.includes('ru')) return 'ru';
    if (lang?.includes('en')) return 'en';
    return 'en';
  };

  useEffect(() => {
    setLanguage(getLanguage());
  }, []);

  const mapSentenses = new Map();
  mapSentenses.set('en', [
    'With smart mode enabled:',
    '1. Absolutely unplayable and understandable hands have been removed.',
    '2. Hands in which a mistake was made are repeated (up to 2 correct answers).',
  ]);
  mapSentenses.set('ru', [
    'При включенном смарт мод:',
    '1. Убраны абсолютно неиграбельные и всем понятные руки.',
    '2. Руки в которых была допущена ошибка повторяются ( до 2х правильных ответов).',
  ]);

  return (
    <>
      {mapSentenses.get(language)?.map((item, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={idx}>
          {item}
        </div>
      ))}
    </>
  );
};

const ControlPanel = ({
  gameResults,
  getResultData,
  setGameResults,
  disable,
  limit,
  setLimit,
  firstTogglerOptions,
  stackSize,
  setStackSize,
  secondTogglerOptions,
  category,
  setCategory,
  positionsGroupedOptions,
  selectedPositions,
  setSelectedPositions,
  selectedHandTypes,
  setSelectedHandTypes,
  disabledPositions,
  huIsDisabled,
  limitIsHU,
  avoidDelimiters,
  handType,
  setHandType,
  handTypeSelector,
  isSmartMode,
  setSmartMode,
  mttIsDisabled,
  stack20isDisabled,
  thirdTogglerOptions,
  isAdaptive = false,
  gameOption,
  setGameOption,
  gameOptions,
  isPlo5,
  fastHands,
  setFastHands,
  timerIsActive,
  setTimerIsActive,
  ggps,
  setGgps,
  isFreeUser,
  difficulty,
  setDifficulty,
  difficultyOptions,
  isMTTAntePotSQZ,
  isMTTAntePot10,
  difficultyIsDisabled,
  sessionActive = false,
}: Props) => {
  const [freeModalVisible, setFreeModalVisible] = useState<boolean>(false);
  const { t } = useTranslation();
  return (
    <Card className={styles.controlPanelWrapper}>
      <fieldset
        disabled={sessionActive}
        className={classnames(styles.challengeFieldset, {
          [styles.challengeFieldsetLocked]: sessionActive,
        })}
      >
        <div className={styles.controlPanelWrap}>
          <div className={styles.smartmodeTitle}>
            <div className={styles.smartmodeQuestion}>
              <span>?</span>
              <div className={styles.smartmodeModal}>
                <ModalText />
              </div>
            </div>
            <div className={styles.controlPanelTimerTitle}>
              <H6><TitleText isFreeUser={isFreeUser} /></H6>
            </div>
          </div>
          <div className={styles.controlPanelSwitch}>
            <PressButton
              text="Smart Mode"
              handleClick={() => (
                isFreeUser ? null : setSmartMode(!isSmartMode)
              )}
              isChecked={isSmartMode}
              customBackground="#565E6C"
              disabled={isFreeUser || timerIsActive}
              setFreeModalVisible={setFreeModalVisible}
              isFreeUser={isFreeUser}
            />
            <PressButton
              text="Fast Mode"
              handleClick={() => (
                isFreeUser ? null : setFastHands(!fastHands)
              )}
              isChecked={fastHands}
              customBackground="#565E6C"
              disabled={isFreeUser || timerIsActive}
              setFreeModalVisible={setFreeModalVisible}
              isFreeUser={isFreeUser}
            />
            <PressButton
              text={t('challengeLabel')}
              textAdd={t('savingResultsLabel')}
              handleClick={() => (
                isFreeUser ? null : setTimerIsActive(!timerIsActive)
              )}
              isChecked={timerIsActive}
              customBackground="#565E6C"
              disabled={isFreeUser}
              setFreeModalVisible={setFreeModalVisible}
              isFreeUser={isFreeUser}
            />
            <FreeModal visible={freeModalVisible} />

            {/* <div className={classnames(styles.controlPanelHandType, styles.control)}>
            <div className={styles.smartmodeTitle}>
              <H4>Smart mode</H4>
              <div className={styles.smartmodeQuestion}>
                <span>?</span>
                <div className={styles.smartmodeModal}>
                  <ModalText />
                </div>
              </div>
            </div>
            <Switch
              switchLabelStyles={styles.switchLabelStyles}
              switchButtonStyles={styles.switchButtonStyles}
              isOn={isSmartMode}
              handleToggle={() => setSmartMode(!isSmartMode)}
            />
          </div>
          <div className={styles.fastTimer}>
            <Check
              label="Fast"
              onCheck={() => setFastHands(!fastHands)}
              checked={fastHands}
              value={fastHands}
              defaultCheck
            />
            <Check
              label={t('challengeLabel')}
              onCheck={() => setTimerIsActive(!timerIsActive)}
              checked={timerIsActive}
              value={timerIsActive}
              defaultCheck
            />
          </div> */}
          </div>
          <div className={styles.controlPanelLimitWrap}>
            <div className={classnames(styles.controlPanelStackSize, styles.control)} style={{ paddingBottom: '15px' }}>
              <Toggler
                selected={difficulty}
                options={difficultyOptions}
                onToggle={(e) => (isFreeUser ? null : setDifficulty(e))}
                className={classnames(styles.gameTogglerColor, styles.firstToggler)}
                controlToggler
                isFreeUser={isFreeUser}
                difficultyIsDisabled={difficultyIsDisabled}
              />
              <Select
                className={styles.adaptiveSelectComponent}
                value={difficulty}
                options={difficultyOptions}
                onChange={setDifficulty}
              />
            </div>
            { isPlo5 && (
            <div className={classnames(styles.controlPanelLimit, styles.control)}>
              <Toggler
                selected={gameOption}
                options={gameOptions}
                onToggle={setGameOption}
                className={classnames(styles.gameTogglerColor, styles.firstToggler)}
                disabled={disable}
                controlToggler
                isFreeUser={isFreeUser}
              />
              <Select
                className={styles.adaptiveSelectComponent}
                disabled={disable}
                value={gameOption}
                options={gameOptions}
                onChange={setGameOption}
              />
            </div>
            )}
            { !isPlo5 && (
            <div className={classnames(styles.controlPanelLimit, styles.control)}>
              <Toggler
                selected={ggps}
                options={[{ label: 'PS', value: 'ps' }, { label: 'GG', value: 'gg' }, { label: 'MTT', value: 'mtt' }, { label: 'HU', value: 'hu' }]}
                onToggle={(e) => (isFreeUser ? null : setGgps(e.value))}
                className={classnames(styles.gameTogglerColor, styles.firstToggler)}
                controlToggler
                isFreeUser={isFreeUser}
              />
              <Select
                className={styles.adaptiveSelectComponent}
                value={ggps}
                options={[{ label: 'PS', value: 'ps' }, { label: 'GG', value: 'gg' }, { label: 'MTT', value: 'mtt' }, { label: 'HU', value: 'hu' }]}
                onChange={(e) => setGgps(e.value)}
              />
            </div>
            )}
            {(!limitIsHU || isPlo5) && (
              <div className={classnames(styles.controlPanelLimit, styles.control)}>
                <H4>Limit</H4>
                <Toggler
                  selected={limit}
                  options={firstTogglerOptions}
                  onToggle={(e) => (isFreeUser ? null : setLimit(e))}
                  className={classnames(styles.gameTogglerColor, styles.firstToggler)}
                  disabled={disable}
                  mttIsDisabled={mttIsDisabled}
                  huIsDisabled={huIsDisabled}
                  controlToggler
                  isFreeUser={isFreeUser}
                />
                <Select
                  className={styles.adaptiveSelectComponent}
                  disabled={disable}
                  value={limit}
                  options={firstTogglerOptions}
                  onChange={setLimit}
                />
              </div>
            )}
            <div className={classnames(styles.controlPanelStackSize, styles.control)}>
              <H4>Stack Size</H4>
              <Toggler
                selected={stackSize}
                options={secondTogglerOptions}
                onToggle={(e) => (isFreeUser ? null : setStackSize(e))}
                className={styles.gameTogglerColor}
                stack20isDisabled={stack20isDisabled}
                selectedTSisAnte={['ante', 'ante3_5x', 'straddle', 'straddle3x'].includes(limit.value)}
                selectedGameTableIsHU={gameOption.value === 'hu'}
                selectedTsIsMedium={(isPlo5 && limit.value === 'low')}
                disabled={disable}
                controlToggler
                isFreeUser={isFreeUser}
                isMTTAntePotSQZ={isMTTAntePotSQZ}
              />
              <Select
                className={styles.adaptiveSelectComponent}
                disabled={disable}
                value={stackSize}
                options={secondTogglerOptions}
                onChange={setStackSize}
              />
            </div>
            <div className={classnames(styles.controlPanelPosition, styles.control)}>
              <H4>Position</H4>
              <Toggler
                selected={category}
                options={thirdTogglerOptions}
                onToggle={(e) => (isFreeUser ? null : setCategory(e))}
                className={styles.gameTogglerColor}
                limitIsHU={limitIsHU}
                disabled={disable}
                controlToggler
                is2rowsToggler={!isAdaptive}
                isFreeUser={isFreeUser}
                isMTTAntePot10={isMTTAntePot10}
              />
            </div>
          </div>
        </div>
        <div className={styles.checkboxesWrapper}>
          <Multicheckbox
            options={positionsGroupedOptions}
            selectedOptions={selectedPositions}
            setSelectedOptions={setSelectedPositions}
            disabledPositions={disabledPositions}
            limitIsHU={limitIsHU}
            isFreeUser={isFreeUser}
            avoidDelimiters={avoidDelimiters}
          />
          <div className={classnames(styles.control, styles.handSelect)}>
            <H4>Hand Type</H4>
            <Multicheckbox
              options={handTypeSelector}
              selectedOptions={selectedHandTypes}
              setSelectedOptions={setSelectedHandTypes}
              isFreeUser={isFreeUser}
              timerIsActive={timerIsActive}
            />
          </div>
        </div>
      </fieldset>
    </Card>
  );
};

export default ControlPanel;
